import { GROUP_ID } from "@/constant/group";
import { LOCAL_STORAGE_KEYS } from "@/constant/keys";
import asyncRetry from "async-retry";
import axios, { AxiosRequestConfig } from "axios";
import { tokenRefreshManager } from "./taskManager";
import { getIsExpiredToken, getIsExpiredCanToken } from "./utils";

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  scope?: string;
}

async function refreshToken({
  groupId,
  refreshToken,
  moimToken,
}: {
  groupId: string;
  refreshToken: string;
  // To be deleted
  moimToken?: string;
}): Promise<TokenResponse> {
  const params = {
    client_id: groupId,
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    moim_token: moimToken,
  };

  try {
    const res = await axios({
      method: "POST",
      url: "https://11plus.vingle.network/api/v2/auth/token",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      data: params,
    });
    const tokens = res.data;
    if (!tokens.access_token) {
      throw new Error("Failed to refresh access token from provider response");
    }
    return tokens;
  } catch (e) {
    if (e instanceof Error) {
      // eslint-disable-next-line no-console
      console.error("OAuth2 token refresh failure was detected: ", e.stack);
    }
    throw e;
  }
}

const refreshAccessToken = async (): Promise<string> => {
  const tokenFromStore = localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN);
  const refreshTokenFromStore = localStorage.getItem(
    LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
  );
  const groupId = GROUP_ID;

  if (!tokenFromStore || !refreshTokenFromStore) {
    throw new Error("not exist group id !");
  }

  const response = await asyncRetry(
    async () => {
      try {
        if (getIsExpiredCanToken(tokenFromStore)) {
          const result = await refreshToken({
            groupId,
            refreshToken: refreshTokenFromStore,
          });
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
            result.refresh_token,
          );
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.AUTH_TOKEN,
            result.access_token,
          );

          return result.access_token;
        } else {
          return tokenFromStore;
        }
      } catch (error) {
        console.error("Failed to refresh access token", error);
        throw error;
      }
    },
    {
      retries: 1,
      minTimeout: 2000,
      maxTimeout: 2000,
      factor: 1,
      randomize: true,
    },
  );

  if (!response) {
    throw new Error("not exist response ! ");
  }
  return response;
};

export const moimApi = (baseURL: string, headers?: Record<string, any>) => {
  const ct = axios.CancelToken;
  const source = ct.source();

  const client = axios.create({
    cancelToken: source.token,
    baseURL: baseURL,
    headers: {
      "Content-Type": "application/json",
      "x-moim-group-id": GROUP_ID,
      ...headers,
    },
  });

  client.interceptors.request.use(async req => {
    const token = req.headers.Authorization as string | undefined;
    try {
      if (token) {
        const isExpired = getIsExpiredToken(token);

        if (isExpired) {
          const manager = tokenRefreshManager.getInstance();

          const promise = new Promise<string | undefined>(resolve => {
            manager.pushTask(newToken => {
              resolve(newToken);
            });
          });

          if (!manager.getIsRefreshing()) {
            manager.startRefreshing();
            try {
              const newToken = await refreshAccessToken();
              manager.finishRefreshing(newToken);
            } catch (error) {
              console.error("Token refresh failed:", error);
              manager.finishRefreshing();
              localStorage.clear();
              location.reload();
            }
          }

          const newToken = await promise;
          if (newToken) {
            req.headers.Authorization = `Bearer ${newToken}`;
          } else {
            delete req.headers.Authorization;
          }
        }
      }
    } catch (_error) {
      console.error("Token refresh error:", _error);
    }
    return req;
  });

  const request = async <T>(
    config: AxiosRequestConfig,
    options: {
      retry?: boolean;
      hubRequest?: boolean;
      headers?: { [key: string]: string };
    } = {},
  ) => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN);
    const userLocale =
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_LOCALE) ??
      window.navigator.language.split("-")[0];

    const { retry = true, headers } = options;

    const data: T | undefined = await asyncRetry(
      async bail => {
        try {
          const response = await client({
            ...config,
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
              ...headers,
              "x-can-user-locale": userLocale,
            },
          });
          return response.data as T;
        } catch (e: any) {
          console.error(e);
          if (!retry) {
            bail(e);
            return;
          }
          if (!e.response.status) {
            bail(e);
            return;
          }

          throw e;
        }
      },
      {
        retries: 0,
      },
    );

    return data!;
  };

  return {
    request,
    cancel: source.cancel,
    cancelToken: source.token,
    client,
    headers: client.defaults.headers,
    baseURL: client.defaults.baseURL,
  };
};
